import React, { useCallback, useState } from "react";
import styled, { keyframes } from "styled-components";
import theme from "theme";
import { developerSurface, sectionIds } from "content/site";
import {
	Container,
	Section,
	SectionTitle,
	SectionLead,
	TextLink,
	PrimaryButton,
	SecondaryButton,
} from "components/marketing/primitives";

const MODE_ID = "dev-surface-mode";
const HOST_ID = "dev-surface-host";

const fadeIn = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: translateY(0); }
`;

const PreviewShell = styled.div`
	background: ${theme.color.darkL1};
	border-radius: ${theme.radius.lg};
	overflow: hidden;
	margin-bottom: 1.35rem;
	box-shadow: ${theme.boxShadow.l};
	border: 1px solid rgba(255, 255, 255, 0.06);
`;

const ChromeBar = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.7rem 1rem;
	background: ${theme.color.darkL2};
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
	flex-wrap: wrap;
`;

const TrafficLights = styled.div`
	display: flex;
	gap: 0.35rem;
	flex-shrink: 0;
`;

const Dot = styled.span`
	width: 0.55rem;
	height: 0.55rem;
	border-radius: 50%;
	background: ${(p) => p.$color};
	opacity: 0.85;
`;

const ChromeTitle = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 600;
	color: ${theme.color.lightD2};
	letter-spacing: 0.02em;
	margin-right: auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const TabList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.25rem;
	width: 100%;
	padding: 0 1rem 0.65rem;
	background: ${theme.color.darkL2};
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
	margin-top: -0.15rem;
`;

const Tab = styled.button`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 600;
	padding: 0.4rem 0.85rem;
	border-radius: ${theme.radius.sm};
	border: 1px solid ${(p) => (p.$active ? "rgba(255, 255, 255, 0.22)" : "transparent")};
	background: ${(p) => (p.$active ? "rgba(255, 255, 255, 0.1)" : "transparent")};
	color: ${(p) => (p.$active ? "#fff" : theme.color.lightD2)};
	cursor: pointer;
	transition:
		background ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		border-color ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		color ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut};

	&:hover {
		background: rgba(255, 255, 255, 0.08);
		color: #fff;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const HostTabList = styled(TabList)`
	background: ${theme.color.darkL1};
	border-bottom-color: rgba(255, 255, 255, 0.05);
	padding-top: 0.65rem;
	margin-top: 0;
`;

const SessionBody = styled.div`
	padding: 1.1rem 1.15rem 1.25rem;
	min-height: 14.5rem;
`;

const SessionFade = styled.div`
	animation: ${fadeIn} ${theme.transitionDuration.normal} ${theme.transitionTimingFunction.easeOut};

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const SdkPre = styled.pre`
	margin: 0;
	padding: 1.1rem 1.15rem;
	overflow-x: auto;
	font-family: ${theme.fontFamily.mono};
	font-size: 0.78rem;
	line-height: 1.55;
	color: ${theme.color.lightD1};
	tab-size: 2;
	min-height: 14.5rem;
	box-sizing: border-box;
`;

const SdkHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0.55rem 1.1rem;
	background: rgba(255, 255, 255, 0.03);
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const SdkLabel = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 700;
	color: ${theme.color.lightD2};
	letter-spacing: 0.02em;
`;

const PanelCopyBtn = styled.button`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 600;
	padding: 0.4rem 0.85rem;
	border-radius: ${theme.radius.sm};
	border: 1px solid rgba(255, 255, 255, 0.2);
	background: rgba(255, 255, 255, 0.08);
	color: #fff;
	cursor: pointer;
	&:hover {
		background: rgba(255, 255, 255, 0.14);
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

const Line = styled.div`
	& + & {
		margin-top: 0.95rem;
	}
`;

const LineMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.35rem;
	flex-wrap: wrap;
`;

const RoleBadge = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.6875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	padding: 0.2rem 0.45rem;
	border-radius: ${theme.radius.sm};
	color: ${(p) => {
		if (p.$role === "tool") return theme.color.secondary;
		if (p.$role === "assistant") return "#7EC8FF";
		if (p.$role === "system") return theme.color.lightD2;
		return theme.color.light;
	}};
	background: ${(p) => {
		if (p.$role === "tool") return "rgba(255, 196, 0, 0.12)";
		if (p.$role === "assistant") return "rgba(0, 119, 204, 0.18)";
		if (p.$role === "system") return "rgba(255, 255, 255, 0.06)";
		return "rgba(255, 255, 255, 0.1)";
	}};
	border: 1px solid
		${(p) => {
			if (p.$role === "tool") return "rgba(255, 196, 0, 0.28)";
			if (p.$role === "assistant") return "rgba(0, 119, 204, 0.35)";
			return "rgba(255, 255, 255, 0.08)";
		}};
`;

const ToolName = styled.code`
	font-family: ${theme.fontFamily.mono};
	font-size: 0.78rem;
	font-weight: 600;
	color: ${theme.color.secondary};
`;

const LineText = styled.p`
	margin: 0;
	font-family: ${(p) => (p.$mono ? theme.fontFamily.mono : theme.fontFamily.sans)};
	font-size: ${(p) => (p.$mono ? "0.78rem" : "0.875rem")};
	line-height: 1.55;
	color: ${theme.color.lightD1};
	white-space: pre-wrap;
	word-break: break-word;
`;

const LineDetail = styled.p`
	margin: 0.3rem 0 0;
	font-family: ${theme.fontFamily.mono};
	font-size: 0.72rem;
	line-height: 1.45;
	color: ${theme.color.grey};
`;

const ConnectStrip = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	gap: 0;
	margin-bottom: 1.35rem;
	background: ${theme.color.darkL1};
	border-radius: ${theme.radius.md};
	border: 1px solid rgba(255, 255, 255, 0.06);
	overflow: hidden;
	box-shadow: ${theme.boxShadow.m};
`;

const ConnectLabel = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	color: ${theme.color.lightD2};
	letter-spacing: 0.02em;
	padding: 0.65rem 0.9rem;
	background: ${theme.color.darkL2};
	border-right: 1px solid rgba(255, 255, 255, 0.06);
	display: flex;
	align-items: center;
	flex-shrink: 0;

	@media (max-width: 576px) {
		width: 100%;
		border-right: none;
		border-bottom: 1px solid rgba(255, 255, 255, 0.06);
	}
`;

const ConnectPre = styled.pre`
	margin: 0;
	flex: 1 1 12rem;
	min-width: 0;
	padding: 0.65rem 0.85rem;
	overflow-x: auto;
	font-family: ${theme.fontFamily.mono};
	font-size: 0.72rem;
	line-height: 1.45;
	color: ${theme.color.lightD1};
	align-self: center;
`;

const StripCopyBtn = styled.button`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 600;
	padding: 0.4rem 0.85rem;
	margin: 0.45rem 0.55rem 0.45rem auto;
	border-radius: ${theme.radius.sm};
	border: 1px solid rgba(255, 255, 255, 0.2);
	background: rgba(255, 255, 255, 0.08);
	color: #fff;
	cursor: pointer;
	flex-shrink: 0;
	align-self: center;
	&:hover {
		background: rgba(255, 255, 255, 0.14);
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}

	@media (max-width: 576px) {
		margin: 0 0.55rem 0.55rem;
	}
`;

const MetaRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1.25rem 2rem;
	margin-bottom: 1.35rem;
`;

const MetaBlock = styled.div`
	min-width: min(100%, 16rem);
	flex: 1 1 16rem;
`;

const MetaLabel = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: ${theme.color.greyD1};
	margin: 0 0 0.45rem;
`;

const Endpoint = styled.code`
	display: inline-block;
	font-family: ${theme.fontFamily.mono};
	font-size: 0.8125rem;
	line-height: 1.45;
	color: ${theme.color.dark};
	background: ${theme.color.surface};
	border: 1px solid ${theme.color.border};
	border-radius: ${theme.radius.sm};
	padding: 0.4rem 0.65rem;
	word-break: break-all;
`;

const ToolList = styled.ul`
	display: flex;
	flex-wrap: wrap;
	gap: 0.45rem;
	list-style: none;
	margin: 0;
	padding: 0;
`;

const ToolChip = styled.li`
	font-family: ${theme.fontFamily.mono};
	font-size: 0.75rem;
	font-weight: 600;
	color: ${theme.color.dark};
	background: ${theme.color.surface};
	border: 1px solid ${theme.color.border};
	border-radius: ${theme.radius.sm};
	padding: 0.35rem 0.55rem;
`;

const Note = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	color: ${theme.color.greyD2};
	margin: 0 0 1.35rem;
	line-height: 1.55;
	max-width: 42rem;
`;

const CtaRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	margin-bottom: 1.25rem;
	align-items: center;
`;

const LinkRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 1rem 1.35rem;
`;

function SessionLine({ line }) {
	const isTool = line.role === "tool";
	const isMono = line.role === "system" || isTool;

	return (
		<Line>
			<LineMeta>
				<RoleBadge $role={line.role}>{line.label}</RoleBadge>
				{isTool ? <ToolName>{line.text}</ToolName> : null}
			</LineMeta>
			{!isTool ? <LineText $mono={isMono}>{line.text}</LineText> : null}
			{line.detail ? <LineDetail>{line.detail}</LineDetail> : null}
		</Line>
	);
}

function makeTabKeyHandler(tabs, setId, idPrefix) {
	return (event, index) => {
		let next = index;
		if (event.key === "ArrowRight" || event.key === "ArrowDown") {
			event.preventDefault();
			next = (index + 1) % tabs.length;
		} else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
			event.preventDefault();
			next = (index - 1 + tabs.length) % tabs.length;
		} else if (event.key === "Home") {
			event.preventDefault();
			next = 0;
		} else if (event.key === "End") {
			event.preventDefault();
			next = tabs.length - 1;
		} else {
			return;
		}
		setId(tabs[next].id);
		const el = document.getElementById(`${idPrefix}-tab-${tabs[next].id}`);
		if (el) el.focus();
	};
}

export default function DeveloperSurface() {
	const [modeId, setModeId] = useState(developerSurface.defaultModeId);
	const [hostId, setHostId] = useState(
		() => developerSurface.modes.find((m) => m.id === "mcp")?.defaultPreviewId || "cursor"
	);
	const [copied, setCopied] = useState(false);

	const activeMode =
		developerSurface.modes.find((m) => m.id === modeId) || developerSurface.modes[0];
	const isMcp = activeMode.id === "mcp";
	const activePreview = isMcp
		? activeMode.previews.find((p) => p.id === hostId) || activeMode.previews[0]
		: null;
	const chromeTitle = isMcp
		? activePreview?.chrome || activeMode.chromeFallback
		: activeMode.chrome;
	const copyText = activeMode.snippet;

	const onCopy = useCallback(() => {
		if (!navigator.clipboard) return;
		navigator.clipboard.writeText(copyText).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [copyText]);

	const onModeKeyDown = useCallback(
		makeTabKeyHandler(developerSurface.modes, setModeId, MODE_ID),
		[]
	);

	const onHostKeyDown = useCallback((event, index) => {
		const mcp = developerSurface.modes.find((m) => m.id === "mcp");
		if (!mcp?.previews) return;
		makeTabKeyHandler(mcp.previews, setHostId, HOST_ID)(event, index);
	}, []);

	return (
		<Section id={sectionIds.developers} aria-labelledby="developers-heading" $alt>
			<Container>
				<SectionTitle id="developers-heading">{developerSurface.title}</SectionTitle>
				<SectionLead>{developerSurface.lead}</SectionLead>

				<PreviewShell>
					<ChromeBar>
						<TrafficLights aria-hidden="true">
							<Dot $color="#FF5F57" />
							<Dot $color="#FEBC2E" />
							<Dot $color="#28C840" />
						</TrafficLights>
						<ChromeTitle>{chromeTitle}</ChromeTitle>
					</ChromeBar>

					<TabList role="tablist" aria-label="Developer surface">
						{developerSurface.modes.map((mode, index) => {
							const selected = mode.id === activeMode.id;
							return (
								<Tab
									key={mode.id}
									id={`${MODE_ID}-tab-${mode.id}`}
									role="tab"
									type="button"
									$active={selected}
									aria-selected={selected}
									aria-controls={`${MODE_ID}-panel-${mode.id}`}
									tabIndex={selected ? 0 : -1}
									onClick={() => {
										setModeId(mode.id);
										setCopied(false);
									}}
									onKeyDown={(e) => onModeKeyDown(e, index)}
								>
									{mode.label}
								</Tab>
							);
						})}
					</TabList>

					{developerSurface.modes.map((mode) => {
						const selected = mode.id === activeMode.id;
						return (
							<div
								key={mode.id}
								id={`${MODE_ID}-panel-${mode.id}`}
								role="tabpanel"
								aria-labelledby={`${MODE_ID}-tab-${mode.id}`}
								hidden={!selected}
							>
								{selected && mode.id === "mcp" ? (
									<>
										<HostTabList role="tablist" aria-label="MCP host examples">
											{mode.previews.map((preview, index) => {
												const hostSelected = preview.id === activePreview.id;
												return (
													<Tab
														key={preview.id}
														id={`${HOST_ID}-tab-${preview.id}`}
														role="tab"
														type="button"
														$active={hostSelected}
														aria-selected={hostSelected}
														aria-controls={`${HOST_ID}-panel-${preview.id}`}
														tabIndex={hostSelected ? 0 : -1}
														onClick={() => setHostId(preview.id)}
														onKeyDown={(e) => onHostKeyDown(e, index)}
													>
														{preview.label}
													</Tab>
												);
											})}
										</HostTabList>
										{mode.previews.map((preview) => {
											const hostSelected = preview.id === activePreview.id;
											return (
												<SessionBody
													key={preview.id}
													id={`${HOST_ID}-panel-${preview.id}`}
													role="tabpanel"
													aria-labelledby={`${HOST_ID}-tab-${preview.id}`}
													hidden={!hostSelected}
												>
													{hostSelected ? (
														<SessionFade key={preview.id}>
															{preview.lines.map((line, i) => (
																<SessionLine key={`${preview.id}-${i}`} line={line} />
															))}
														</SessionFade>
													) : null}
												</SessionBody>
											);
										})}
									</>
								) : null}

								{selected && mode.id === "sdk" ? (
									<SessionFade key="sdk">
										<SdkHead>
											<SdkLabel>{mode.snippetLabel}</SdkLabel>
											<PanelCopyBtn type="button" onClick={onCopy}>
												{copied ? "Copied" : "Copy"}
											</PanelCopyBtn>
										</SdkHead>
										<SdkPre>
											<code>{mode.snippet}</code>
										</SdkPre>
									</SessionFade>
								) : null}
							</div>
						);
					})}
				</PreviewShell>

				{isMcp ? (
					<>
						<ConnectStrip>
							<ConnectLabel>{activeMode.snippetLabel}</ConnectLabel>
							<ConnectPre>
								<code>{activeMode.snippet}</code>
							</ConnectPre>
							<StripCopyBtn type="button" onClick={onCopy}>
								{copied ? "Copied" : "Copy"}
							</StripCopyBtn>
						</ConnectStrip>
						<MetaRow>
							<MetaBlock>
								<MetaLabel>{activeMode.endpointLabel}</MetaLabel>
								<Endpoint>{activeMode.endpoint}</Endpoint>
							</MetaBlock>
							<MetaBlock>
								<MetaLabel>{activeMode.toolsLabel}</MetaLabel>
								<ToolList>
									{activeMode.tools.map((tool) => (
										<ToolChip key={tool}>{tool}</ToolChip>
									))}
								</ToolList>
							</MetaBlock>
						</MetaRow>
						<Note>{activeMode.eligibilityNote}</Note>
					</>
				) : null}

				<CtaRow>
					{activeMode.primaryCta ? (
						<PrimaryButton href={activeMode.primaryCta.href}>
							{activeMode.primaryCta.label}
						</PrimaryButton>
					) : null}
					{activeMode.secondaryCta ? (
						<SecondaryButton href={activeMode.secondaryCta.href}>
							{activeMode.secondaryCta.label}
						</SecondaryButton>
					) : null}
				</CtaRow>
				<LinkRow>
					{(activeMode.links || []).map((l) => (
						<TextLink key={l.href} href={l.href}>
							{l.label}
						</TextLink>
					))}
				</LinkRow>
			</Container>
		</Section>
	);
}
